import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { MeetingRoom } from '../../types/models';
import { formatClosureDate, isRoomClosed } from '../../utils/roomAvailability';

interface RoomClosureNoticeModalProps {
  open: boolean;
  /** 예약 가능 기간이 남아 있는 회의실 (utils/roomAvailability 의 getClosingRooms 결과) */
  rooms: MeetingRoom[];
  onClose: () => void;
  /** '오늘 하루 보지 않기'를 체크한 채 닫았을 때 */
  onDismissToday: () => void;
}

const RoomClosureNoticeModal: React.FC<RoomClosureNoticeModalProps> = ({
  open,
  rooms,
  onClose,
  onDismissToday
}) => {
  const [hideForToday, setHideForToday] = useState(false);

  const handleClose = () => {
    if (hideForToday) {
      onDismissToday();
    }
    onClose();
  };

  if (rooms.length === 0) return null;

  return (
    <Dialog
      open={open}
      // 바깥을 누르거나 Esc 로 닫히면 안내를 못 보고 지나치게 되므로 확인 버튼으로만 닫는다.
      // '오늘 하루 보지 않기' 체크도 확인을 눌러야 저장되니 닫는 길이 하나여야 헷갈리지 않는다.
      onClose={(_event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        handleClose();
      }}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          // 좁은 화면에서는 기본 여백이 커서 본문이 더 좁아진다. 여백을 줄여 폭을 돌려준다.
          m: { xs: 2, sm: 4 },
          width: { xs: 'calc(100% - 32px)', sm: 'auto' },
          // 한글은 기본값이면 글자 단위로 끊겨 '부탁드립니/다.' 처럼 갈라진다.
          // 띄어쓰기 단위로만 끊기게 한다.
          wordBreak: 'keep-all'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <EventBusyIcon color="primary" fontSize="small" />
        <Typography component="span" sx={{ fontWeight: 600, fontSize: '1.05rem' }}>
          회의실 예약 기간 안내
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          아래 회의실은 예약 가능 기간이 다음과 같이 제한됩니다.
        </Typography>

        <Box
          component="ul"
          sx={{
            listStyle: 'none',
            m: 0,
            mt: 1.5,
            p: 1.5,
            bgcolor: 'grey.50',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2
          }}
        >
          {rooms.map(room => (
            <Box
              component="li"
              key={room.id}
              sx={{ display: 'flex', gap: 1, '& + &': { mt: 1 } }}
            >
              {/* 사이드바의 회의실 표시와 같은 색 동그라미를 써서 어느 방인지 바로 알아보게 한다 */}
              <Box
                component="span"
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: room.color || '#cccccc',
                  flexShrink: 0,
                  mt: '5px'
                }}
              />
              {/*
                좁은 화면에서는 회의실 이름과 기간을 줄을 나눠 보여준다.
                한 줄에 다 넣으면 이름 중간에서 어색하게 끊긴다.
                글씨를 줄이는 대신 줄을 나누는 쪽이 읽기 쉽다.
              */}
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="body2"
                  component="span"
                  sx={{ display: { xs: 'block', sm: 'inline' } }}
                >
                  {room.name}
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    {' : '}
                  </Box>
                </Typography>
                <Typography
                  variant="body2"
                  component="span"
                  sx={{ display: { xs: 'block', sm: 'inline' } }}
                >
                  {/*
                    날짜가 이 안내의 핵심이므로 본문보다 눈에 띄게 둔다.
                    이미 끝난 회의실은 파랑(안내)이 아니라 회색으로 두어
                    "아직 쓸 수 있다"로 읽히지 않게 한다.
                  */}
                  {isRoomClosed(room) ? (
                    <Box component="span" sx={{ color: 'text.disabled', fontWeight: 700 }}>
                      {formatClosureDate(room)}로 예약 종료
                    </Box>
                  ) : (
                    <>
                      <Box component="span" sx={{ color: 'primary.main', fontWeight: 700 }}>
                        {formatClosureDate(room)}까지
                      </Box>{' '}
                      예약 가능
                    </>
                  )}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          이후 일정은 다른 회의실로 예약 부탁드립니다.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, pt: 0, justifyContent: 'space-between' }}>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={hideForToday}
              onChange={event => setHideForToday(event.target.checked)}
            />
          }
          label={
            <Typography variant="caption" color="text.secondary">
              오늘 하루 보지 않기
            </Typography>
          }
        />
        <Button onClick={handleClose} variant="contained" size="small" disableElevation>
          확인
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoomClosureNoticeModal;
